/**
 * T246 — /depreciations list page (US5).
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import {
  Button,
  EmptyState,
  GlassPanel,
  Input,
  LoadingSpinner,
  Modal,
  PageHeader,
  StatusBadge,
  type StatusTone,
} from '../components/ui';
import {
  useDepreciations,
  useTriggerSonderAfA,
  type DepreciationPlanDto,
} from '../services/api/depreciations';
import { useToastHelpers } from '../components/ui/Toast';
import { formatCurrency } from '@armard/shared';
import { formatDateBerlin } from '../lib/format-date';
import { apiErrorMessage } from '../lib/api-error-message';

export const Route = createFileRoute('/depreciations')({
  component: DepreciationsPage,
});

function DepreciationsPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <DepreciationsInner />
      </AppShell>
    </ProtectedRoute>
  );
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Aktiv',
  completed: 'Abgeschlossen',
  sonderafa: 'Sonder-AfA',
};

// 006b P5 — Kanon-Tone-Map (PATTERNS.md §8): grün=aktiv laufend,
// blau=regulär abgeschlossen, lila=Sonderereignis.
const STATUS_TONES: Record<string, StatusTone> = {
  active: 'success',
  completed: 'info',
  sonderafa: 'special',
};

function DepreciationsInner() {
  const { data, isLoading } = useDepreciations();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Abschreibungen"
        subtitle={`Linearer AfA-Plan pro Beleg · ${data?.items.length ?? 0} Pläne`}
      />

      {isLoading && <LoadingSpinner />}

      {data && data.items.length === 0 && (
        <EmptyState
          title="Keine Abschreibungspläne"
          description="AfA-Pläne entstehen automatisch, sobald ein Beleg einer abschreibungs-Kategorie zugeordnet wird."
        />
      )}

      {data && data.items.length > 0 && (
        <GlassPanel
          variant="subtle"
          padding="sm"
          className="overflow-hidden"
          data-testid="depreciations-list-surface"
        >
          <div className="space-y-2">
            {data.items.map((p) => (
              <PlanRow key={p.id} plan={p} />
            ))}
          </div>
        </GlassPanel>
      )}
    </div>
  );
}

function PlanRow({ plan }: { plan: DepreciationPlanDto }) {
  const trigger = useTriggerSonderAfA(plan.id);
  const toast = useToastHelpers();
  const [sonderAfaOpen, setSonderAfaOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [effectiveDate, setEffectiveDate] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);

  async function submitSonderAfA() {
    const trimmedReason = reason.trim();
    if (trimmedReason.length < 5) {
      setReasonError('Bitte eine Begründung mit mindestens 5 Zeichen eingeben.');
      return;
    }
    try {
      await trigger.mutateAsync({
        reason: trimmedReason,
        ...(effectiveDate ? { effectiveDate } : {}),
      });
      toast.success('Sonder-AfA ausgelöst — Plan abgeschlossen');
      setSonderAfaOpen(false);
      setReason('');
      setEffectiveDate('');
      setReasonError(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Sonder-AfA fehlgeschlagen.'));
    }
  }

  const pctComplete =
    Number(plan.netAmount) > 0
      ? Math.round((plan.cumulativeAmount / Number(plan.netAmount)) * 100)
      : 0;

  return (
    <>
      <div className="p-4 bg-white/5 border border-white/10 rounded-lg">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-white font-medium">
              {formatCurrency(Number(plan.netAmount))} · {plan.totalMonths} Monate · Rate{' '}
              {formatCurrency(Number(plan.monthlyRate))}
            </div>
            <div className="text-white/50 text-sm">
              Start {formatDateBerlin(plan.startDate)} · {plan.monthsElapsed}/{plan.totalMonths}{' '}
              Monate gebucht · Rest {formatCurrency(plan.remainingAmount)}
            </div>
            <Link
              to="/expenses/$id"
              params={{ id: plan.expenseId }}
              className="inline-flex mt-2 text-sm text-blue-200 hover:text-white"
            >
              Ursprungsausgabe öffnen →
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge tone={STATUS_TONES[plan.status] ?? 'neutral'}>
              {STATUS_LABELS[plan.status] ?? plan.status}
            </StatusBadge>
            {plan.status === 'active' && (
              <Button
                variant="ghost"
                onClick={() => {
                  setReason('');
                  setEffectiveDate('');
                  setReasonError(null);
                  setSonderAfaOpen(true);
                }}
                disabled={trigger.isPending}
              >
                {trigger.isPending ? '…' : 'Sonder-AfA'}
              </Button>
            )}
          </div>
        </div>
        <div className="mt-2 h-1.5 bg-white/10 rounded-full overflow-hidden">
          <div className="h-full bg-blue-400 transition-all" style={{ width: `${pctComplete}%` }} />
        </div>
        {plan.sonderafaReason && (
          <div className="text-xs text-white/40 mt-2">Sonder-AfA: {plan.sonderafaReason}</div>
        )}
      </div>
      <Modal
        isOpen={sonderAfaOpen}
        onClose={() => setSonderAfaOpen(false)}
        title="Sonder-AfA auslösen"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void submitSonderAfA();
          }}
          className="space-y-4 p-6"
        >
          <p className="text-sm text-white/60">
            Bitte begründe die vollständige Sonderabschreibung dieses Plans.
          </p>
          <Input
            label="Begründung *"
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              if (reasonError) setReasonError(null);
            }}
            hint="Mindestens 5 Zeichen, z. B. Defekt nach Wasserschaden"
            error={reasonError ?? undefined}
            required
          />
          <Input
            label="Wirksamkeitsdatum (optional)"
            type="date"
            value={effectiveDate}
            onChange={(event) => setEffectiveDate(event.target.value)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setSonderAfaOpen(false)}>
              Abbrechen
            </Button>
            <Button type="submit" loading={trigger.isPending}>
              Sonder-AfA bestätigen
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}
